import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Collapse, alpha, makeStyles } from '@material-ui/core';
import DotsMenu from '../../../../dotsMenu';
import CustomButton from '../../../../customButton';
import Level from './level';
import SalaryTooltip from '../../../../salaryTooltip';
import ReorderItems from '../../../../reorderItems';
import ActionButton from '../../../../actionButton';
import TextBoxWithTooltip from '../../../../textBoxWithTooltip';
import { ACTION_BUTTON_TYPES } from '../../../../actionButton/config';
import { isArrayEmpty, reorder } from '../../../../../../utility/helpers';
import { prepareDotsMenuItems } from '../../../../../../utility/leveling';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  header: {
    cursor: 'pointer',
    borderRadius: 8,
    backgroundColor: primary.bluish9,
    display: 'grid',
    gridTemplateColumns:
      'minmax(36px, 36px) minmax(380px, 1fr) minmax(60px, 180px) minmax(160px, 170px) minmax(60px, 60px)',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 32,
    marginBottom: spacing(3),
    '& $dragButton': {
      opacity: 0,
      transition: 'opacity .3s ease',
    },
    '&:active': {
      '& > div:first-of-type': {
        opacity: 1,
        transition: 'opacity .3s ease',
      },
      boxShadow: `0px 2px 8px 0px ${alpha(primary.black, 0.17)}`,
      transition: 'box-shadow .3s ease',
    },
    '&:hover': {
      '& $dragButton': {
        opacity: ({ isVisible }) => {
          return isVisible ? 1 : 0;
        },
        transition: 'opacity .3s ease',
        cursor: ({ isVisible }) => {
          return isVisible ? 'pointer' : 'grabbing';
        },
      },
      boxShadow: `0px 2px 8px 0px ${alpha(primary.black, 0.17)}`,
      transition: 'box-shadow .3s ease',
    },
  },
  headerNoSalary: {
    gridTemplateColumns:
      'minmax(380px, 1fr) minmax(60px, 165px) minmax(30px, 30px)',
  },
  headerNoSalaryWithEdit: {
    gridTemplateColumns:
      'minmax(36px, 36px) minmax(380px, 1fr) minmax(60px, 165px) minmax(60px, 60px)',
  },
  headerNoSalaryWithDelete: {
    gridTemplateColumns:
      'minmax(380px, 1fr) minmax(60px, 165px) minmax(60px, 60px)',
  },
  headerSalary: {
    gridTemplateColumns:
      'minmax(380px, 1fr) minmax(60px, 180px) minmax(160px, 170px) minmax(30px, 30px)',
  },
  headerSalaryWithEdit: {
    gridTemplateColumns:
      'minmax(36px, 36px) minmax(380px, 1fr) minmax(60px, 180px) minmax(160px, 170px) minmax(60px, 60px)',
  },
  headerSalaryWithDelete: {
    gridTemplateColumns:
      'minmax(380px, 1fr) minmax(60px, 180px) minmax(160px, 170px) minmax(60px, 60px)',
  },
  templateHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  headerDisabled: {
    gridTemplateColumns:
      'minmax(360px, 1fr) minmax(60px, 180px) minmax(180px, 180px) minmax(30px, 40px)',
  },
  disabledNoSalary: {
    gridTemplateColumns:
      'minmax(360px, 1fr) minmax(60px, 180px) minmax(0px, 0px) minmax(60px, 60px)',
  },
  disableCollapse: {
    cursor: 'default',
  },
  actionsWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
    paddingRight: spacing(2),
    justifyContent: 'flex-end',
  },
  actionButton: {
    width: 24,
    height: 24,
  },
  templateActionButton: {
    marginRight: spacing(2),
  },
  expandIcon: {
    transform: 'rotate(0deg)',
    transition: 'transform .3s cubic-bezier(0.77, 0.2, 0.05, 1)',
  },
  expandIconOpened: {
    transform: 'rotate(180deg)',
  },
  button: {
    borderBottom: `1px solid ${primary.bluish7}`,
    borderTop: `1px solid ${primary.bluish7}`,
    borderRadius: 'unset',
    marginBottom: 14,
    width: '100%',
    height: 40,
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: primary.white,
    },
  },
  dragButton: {
    position: 'relative',
    width: 22,
    height: 22,
    marginLeft: spacing(2),
    flexShrink: 0,
    marginRight: spacing(1.5),
    zIndex: 11,
    '& button': {
      width: 22,
      height: 22,
    },
    '$header:hover': {
      opacity: ({ isVisible }) => {
        return isVisible ? 1 : 0;
      },
      transition: 'opacity .3s ease',
      cursor: ({ isVisible }) => {
        return isVisible ? 'pointer' : 'grabbing';
      },
    },
  },
  dragIcon: {
    width: 8,
    height: 14,
  },
  dragTooltipLabel: {
    fontFamily: 'ProximaNova-Regular',
  },
  cell: {
    marginLeft: 0,
    fontSize: 14,
    lineHeight: '18px',
  },
  disabledActions: {
    cursor: 'default',
    justifyContent: 'end',
  },
  titleText: {
    boxSizing: 'border-box',
    fontSize: 14,
    lineHeight: '18px',
    paddingLeft: spacing(9),
    paddingRight: spacing(1.5),
  },
  titleWithDragButton: {
    paddingLeft: 0,
  },
  levelItem: {
    '&:last-of-type': {
      '& > div': {
        borderRadius: 8,
        borderBottom: 'none',
        position: 'relative',
      },
    },
  },
  salaryIcon: {
    marginRight: 'auto',
  },
}));

const LevelingTrack = ({
  translations,
  levelingFramework,
  levelingTrack,
  dragRef = () => {},
  dragProps = null,
  isReadOnly,
  canEdit = false,
  canDelete = false,
  isVisible = false,
  isExpandedOnFirstOpen = false,
  onEditTrackTitle = () => {},
  onDuplicateTrack = () => {},
  handleDeleteTrackDialog = () => {},
  handleCreateLevel = () => {},
  handleMoveTrack = () => {},
  handleEditSalaries = () => {},
  onReorderLevel = () => {},
  toggleExpansion = () => {},
}) => {
  const classes = useStyles({ isVisible });
  const { name, levels } = levelingTrack;
  const hasLevels = !isArrayEmpty(levels);
  const hasSalarySettings = !!levelingFramework.currency;

  const [isExpanded, setIsExpanded] = useState(isExpandedOnFirstOpen || false);
  const isExpandedDropdown = isExpanded || isExpandedOnFirstOpen;
  const toggleExpand = () => {
    if (isExpandedOnFirstOpen) {
      toggleExpansion(false);
      setIsExpanded(false);
    }
    if (!isExpandedOnFirstOpen) {
      setIsExpanded(prevExpanded => !prevExpanded);
    }
  };

  const handleReorder = useCallback(
    result => {
      if (
        !result.destination ||
        result.source.index === result.destination.index
      )
        return;

      const updatedOrder = reorder(
        levels,
        result.source.index,
        result.destination.index
      );

      return onReorderLevel(levelingFramework, levelingTrack.id, updatedOrder);
    },
    [levels, levelingTrack, levelingFramework, onReorderLevel]
  );

  return (
    <div ref={dragRef} {...(dragProps ? { ...dragProps.draggableProps } : {})}>
      <div
        className={classNames(classes.header, {
          [classes.headerNoSalaryWithEdit]: !hasSalarySettings && canEdit,
          [classes.headerNoSalaryWithDelete]:
            !hasSalarySettings && canDelete && !canEdit,
          [classes.headerNoSalary]:
            !hasSalarySettings && !canEdit && !canDelete,
          [classes.headerSalary]: hasSalarySettings && !canEdit && !canDelete,
          [classes.headerSalaryWithEdit]: hasSalarySettings && canEdit,
          [classes.headerSalaryWithDelete]:
            hasSalarySettings && !canEdit && canDelete,
          [classes.disableCollapse]: !hasLevels,
          [classes.templateHeader]: isReadOnly,
        })}
        onClick={toggleExpand}
      >
        {!isReadOnly && canEdit && (
          <ActionButton
            className={classes.dragButton}
            iconClass={classes.dragIcon}
            tooltipLabelClass={classes.dragTooltipLabel}
            type={ACTION_BUTTON_TYPES.DRAG}
            tooltipText={translations.trackDragTooltip}
            {...(dragProps ? { ...dragProps.dragHandleProps } : {})}
          />
        )}
        <TextBoxWithTooltip
          className={classNames(classes.titleText, {
            [classes.titleWithDragButton]: !isReadOnly && canEdit,
          })}
          variant="h5"
          text={name}
        />
        {!isReadOnly && (
          <>
            <TextBoxWithTooltip
              className={classes.cell}
              variant="h5"
              text={translations.people}
            />

            {hasSalarySettings && (
              <div className={classes.salaryIcon}>
                <SalaryTooltip
                  translations={translations}
                  currency={levelingFramework.currency}
                  title={`${levelingFramework.name} ${translations.salaryRange}`}
                  position="bottom-end"
                  track={levelingTrack}
                  canEdit={canEdit}
                  disableHoverListener={!hasLevels}
                  onClick={() =>
                    handleEditSalaries(levelingTrack, levelingFramework)
                  }
                  interactive
                />
              </div>
            )}
          </>
        )}
        <div
          className={classNames(classes.actionsWrapper, {
            [classes.disabledActions]: isReadOnly || !hasLevels,
            [classes.templateActionButton]: !hasLevels,
          })}
        >
          {(canEdit || canDelete) && (
            <DotsMenu
              menuItems={prepareDotsMenuItems(
                translations,
                levelingTrack,
                levelingFramework,
                canEdit,
                canDelete,
                handleDeleteTrackDialog,
                onEditTrackTitle,
                onDuplicateTrack,
                handleMoveTrack,
                true,
                {},
                handleEditSalaries
              )}
              isDisabled={isReadOnly}
            />
          )}
          {(hasLevels || canEdit) && (
            <ActionButton
              className={classNames(
                { [classes.templateActionButton]: isReadOnly },
                classes.actionButton
              )}
              type={ACTION_BUTTON_TYPES.ARROW}
              iconClass={classNames(classes.expandIcon, {
                [classes.expandIconOpened]: isExpandedDropdown,
              })}
            />
          )}
        </div>
      </div>
      <Collapse in={isExpandedDropdown} timeout="auto" unmountOnExit>
        <div>
          {hasLevels && (
            <ReorderItems
              onSave={handleReorder}
              itemClass={classes.levelItem}
              isDragDisabled={isReadOnly || !canEdit}
            >
              {levels.map(level => (
                <Level
                  key={`level_item_${level.id}`}
                  id={level.id}
                  translations={translations}
                  level={level}
                  currency={levelingFramework.currency}
                  isReadOnly={isReadOnly}
                  hasSalarySettings={hasSalarySettings}
                  canEdit={canEdit}
                  canDelete={canDelete}
                />
              ))}
            </ReorderItems>
          )}
          {!isReadOnly && canEdit && (
            <CustomButton
              className={classes.button}
              type="addWithTextDarkNewSmall"
              onClick={() =>
                handleCreateLevel(levelingTrack, levelingFramework)
              }
            >
              {translations.createLevelButton}
            </CustomButton>
          )}
        </div>
      </Collapse>
    </div>
  );
};

LevelingTrack.propTypes = {
  translations: PropTypes.object.isRequired,
  levelingFramework: PropTypes.object.isRequired,
  levelingTrack: PropTypes.object.isRequired,
  dragProps: PropTypes.object,
  isExpandedOnFirstOpen: PropTypes.bool,
  isVisible: PropTypes.bool,
  isReadOnly: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  handleDeleteTrackDialog: PropTypes.func,
  onEditTrackTitle: PropTypes.func,
  onDuplicateTrack: PropTypes.func,
  onReorderLevel: PropTypes.func,
  dragRef: PropTypes.func,
  handleCreateLevel: PropTypes.func,
  handleMoveTrack: PropTypes.func,
  handleEditSalaries: PropTypes.func,
  toggleExpansion: PropTypes.func,
};

export default LevelingTrack;
